/**
 * Invalidate the Actions Log (journal) query once when a polled AI action
 * transitions to `complete`.
 *
 * The AI-action poll hooks (ranking-run, interview-analysis, interview-prep)
 * resolve their `status` to `complete` and then stop polling. Without this,
 * a finished action does not surface in the Actions Log until the user
 * navigates (Card 2 §7 — the missing cache-invalidation). This hook watches
 * the status and fires a single `invalidateQueries({ queryKey: ["journal"] })`
 * on the queued/running → complete edge, so the table refreshes in place.
 *
 * `["journal"]` is the shared prefix of every `useHiringJournal` cache key
 * (`["journal", tenantSlug, ...]`), so a prefix invalidation refetches all
 * journal filter variants.
 */
import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";

export function useInvalidateJournalOnComplete(
  status: string | null | undefined,
): void {
  const queryClient = useQueryClient();
  const prev = useRef<string | null | undefined>(status);

  useEffect(() => {
    if (status === "complete" && prev.current !== "complete") {
      void queryClient.invalidateQueries({ queryKey: ["journal"] });
    }
    prev.current = status;
  }, [status, queryClient]);
}
